import React from 'react';
import { merge } from 'lodash/fp';

import _ from '@ihme/common/locale';
import styled from '@ihme/common/theme/styled';
import { BarChart } from '@ihme/common/web/components';
import getTitleAndSubtitle from '@ihme/common/web/utility/echarts-helpers/get-adopted-title-and-subtitle';

import config from '../../config';

import { renderChartCopyright } from '../../utility/chart-copyright';
import formatChartValue from '../../utility/formatting/formatChartValue';
import formatChartLabel from '../../utility/formatting/formatChartLabel';

import echartsTheme from '../../theme/echarts';
import { pngIcon } from '../../theme/icons';

const BarChartContainer = styled.div(({ theme }) => ({
    padding: '20px 0',
    height: 660,
    [theme.breakpoint.xs]: {
        height: 770,
    },
}));

type Props = {};

class BreakdownChart extends React.Component<Props> {
    renderTitle = (filters) =>
        _('breakdown_chart_title', {
            measure: _(`measure_${filters.measure_id}`),
            metric: _(`metric_${filters.metric_id}`),
            year: filters.year,
        });

    renderSubtitle = (filters) =>
        _('breakdown_chart_subtitle', {
            location: this.getLocalizedFilter(filters.location_id, 'location_'),
            ageGroup: _(`age_group_${filters.age_group_id}`),
        });

    renderTooltip = (params, { isMobileView }) => {
        const { columns, record } = params.data.rawData;

        const getRecordValue = (key: string): string | number => {
            const idx = columns.indexOf(key);
            const recordValue = record[idx];

            return (key.includes('_id'))
                ? _(`${key.slice(0, -3)}_${recordValue}`)
                : recordValue;
        };

        const year = getRecordValue('year');
        const cause = getRecordValue('cause_id');
        const measure = getRecordValue('measure_id');
        const metric = getRecordValue('metric_id');
        const ageGroup = getRecordValue('age_group_id');
        const location = getRecordValue('location_id');
        const value = formatChartValue(getRecordValue('value'));
        const lower = formatChartValue(getRecordValue('lower'));
        const upper = formatChartValue(getRecordValue('upper'));

        const uncertainty = upper !== null && lower !== null ? `(${lower} - ${upper})` : '';
        const valueWithUncertainty = `${value} ${uncertainty}`;

        return (
            `<div class='chart-tooltip-text' style='${isMobileView ? `font-size: 14px` : ''}'>
                <div class='chart-tooltip-title'>${year}</div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        Cause name:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${cause}`}
                    </span>
                </div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        Location:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${location}`}
                    </span>
                </div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        Age:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${ageGroup}`}
                    </span>
                </div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        ${measure} ${metric}:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${valueWithUncertainty}`}
                </div>
            </div>`
        );
    };

    getSaveFilename = ({ filters: { measure_id, metric_id, location_id, age_group_id, year } }) =>
        'Breakdown Chart, Cardiovascular Diseases' +
        this.getLocalizedFilter(measure_id, 'measure_', ', ') +
        this.getLocalizedFilter(metric_id, 'long_metric_', ', ') +
        this.getLocalizedFilter(location_id, 'location_', ' in ') +
        this.getLocalizedFilter(age_group_id, 'age_group_', ', ') +
        ' in ' +
        year;

    getLocalizedFilter = (
        value: string | number,
        localePrefix: string,
        prefix: string = ''
    ): string => {
        const localize = (source): string => source
            ? source.toString()
                .split(',')
                .map(id => _(`${localePrefix}${id}`))
                .join(',')
            : '';

        return prefix +
            value.toString()
                .split(',')
                .map(el => localize(el))
                .join(', ');
    };

    render() {
        return (
            <BarChartContainer>
                <BarChart
                    theme={merge(
                        echartsTheme,
                        {
                            bar: {
                                seriesColorSpectrum: echartsTheme.colorPalette,
                            },
                            noData: {
                                ...echartsTheme.noData,
                                top: '35%',
                            },
                        }
                    )}
                    configureLegend={({ isMobileView }) => ({
                        ...echartsTheme.bar.legend,
                        top: 460,
                        ...(isMobileView && {
                            orient: 'vertical',
                            textStyle: {
                                fontSize: 13,
                            },
                        }),
                    })}
                    configureTitles={({ filters, chartWidth }) => (
                        [
                            getTitleAndSubtitle(
                                this.renderTitle(filters),
                                this.renderSubtitle(filters),
                                chartWidth,
                                document.getElementById('canvasFontSizer'),
                                echartsTheme.title
                            ),
                        ]
                    )}
                    renderTooltip={this.renderTooltip}
                    renderGraphic={renderProps =>
                        renderChartCopyright(renderProps, 76, renderProps.isMobileView ? 280 : 170)
                    }
                    mobileViewWidth={680}
                    xAxisKey="location_id"
                    formatXAxisLabel={id => _('location_' + id)}
                    yAxisKey="value"
                    formatYAxisLabel={formatChartLabel}
                    dataKey="cause_id"
                    multipleChartByKey="gender_id"
                    multipleChartByKeyIds={[config.femaleGenderId]}
                    formatLegendLabel={id => _(`cause_${id}`)}
                    renderYAxisName={({ filters: { metric_id, measure_id } }) =>
                        _(`measure_${measure_id}_full_name`) + ', ' + _(`metric_${metric_id}`)
                    }
                    saveAsImage={{
                        visible: true,
                        filename: this.getSaveFilename,
                        icon: pngIcon,
                    }}
                />
                <canvas id="canvasFontSizer" width="0" height="0" />
            </BarChartContainer>
        );
    }
}

export default BreakdownChart;
